/**
 * TanStack Query hooks for `custom-attribution-windows` (FS-249).
 * Wraps ./api; never touches `fetch` directly.
 */
import {
	useMutation,
	useQuery,
	useQueryClient,
	type UseMutationResult,
	type UseQueryResult,
} from "@tanstack/react-query";
import {
	create,
	list,
	listPresets,
	remove,
	customAttributionWindowsQueryKeys,
	type PresetsResponse,
	type UpsertWindowBody,
	type WindowView,
} from "./api";

export function useCustomAttributionWindowsList(): UseQueryResult<
	WindowView,
	Error
> {
	return useQuery({
		queryKey: customAttributionWindowsQueryKeys.list(),
		queryFn: list,
	});
}

export function useCustomAttributionWindowsPresets(): UseQueryResult<
	PresetsResponse,
	Error
> {
	return useQuery({
		queryKey: customAttributionWindowsQueryKeys.presets(),
		queryFn: listPresets,
	});
}

export function useCustomAttributionWindowsCreate(): UseMutationResult<
	WindowView,
	Error,
	UpsertWindowBody
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: create,
		onSuccess: () => {
			qc.invalidateQueries({
				queryKey: customAttributionWindowsQueryKeys.all,
			});
		},
	});
}

export function useCustomAttributionWindowsDelete(): UseMutationResult<
	WindowView,
	Error,
	string
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: () => remove(),
		onSuccess: () => {
			qc.invalidateQueries({
				queryKey: customAttributionWindowsQueryKeys.all,
			});
		},
	});
}
